import {
  type BigDecimal,
  createBigDecimal,
  minus,
  plus,
} from '@0xtorch/big-decimal'
import type { Action, CryptoCurrency, Nft, Transfer } from '@0xtorch/core'
import { z } from 'zod'
import {
  createXrplAddressFromAccountId,
  createXrplSourceId,
  xrplClassicAddressSchema,
} from './address'
import { xrplTransactionSchema } from './rpc'

/** XRP の既存 CoinGecko 価格 ID。 */
export const xrplXrp: CryptoCurrency = {
  type: 'CryptoCurrency',
  id: 'ripple',
  name: 'XRP',
  symbol: 'XRP',
  market: { coingeckoId: 'ripple' },
  updatedAt: 0,
}
/** 確定 metadata 内の ledger entry 変化。 */
const nodeSchema = z.object({
  LedgerEntryType: z.string(),
  LedgerIndex: z.string().regex(/^[A-Fa-f0-9]{64}$/),
  NewFields: z.record(z.unknown()).optional(),
  FinalFields: z.record(z.unknown()).optional(),
  PreviousFields: z.record(z.unknown()).optional(),
})
/** 有限桁の XRPL IOU 金額。指数部の不正・過大値も拒否する。 */
const decimalSchema = z
  .string()
  .regex(/^-?\d+(?:\.\d+)?(?:[eE][+-]?\d{1,3})?$/)
  .refine((value) => {
    const exponent = Number(value.split(/[eE]/)[1] ?? '0')
    return exponent >= -96 && exponent <= 80
  })
/** IOU の通貨と発行者。 */
const issueSchema = z.object({
  currency: z
    .string()
    .regex(/^(?:[A-Za-z0-9?!@#$%^&*<>(){}[\]|]{3}|[A-Fa-f0-9]{40})$/),
  issuer: xrplClassicAddressSchema,
})
/** IOU の ledger balance。 */
const balanceSchema = z.object({ currency: z.string(), value: decimalSchema })

/** Payment の資産識別子を検証し、発行・償還の相手先補完に利用する。 */
const paymentAssetSchema = z.union([
  z
    .string()
    .regex(/^\d+$/)
    .transform(() => ({ id: xrplXrp.id, issuer: undefined })),
  issueSchema.extend({ value: decimalSchema }).transform((amount) => ({
    id: `xrpl:iou:${amount.issuer}:${amount.currency.length === 40 ? amount.currency.toUpperCase() : amount.currency}`,
    issuer: amount.issuer,
  })),
  z
    .object({
      mpt_issuance_id: z.string().regex(/^[A-Fa-f0-9]{48}$/),
      value: z.string().regex(/^\d+$/),
    })
    .transform((amount) => ({
      id: `xrpl:mpt:${amount.mpt_issuance_id.toUpperCase()}`,
      issuer: createXrplAddressFromAccountId({
        accountId: amount.mpt_issuance_id.slice(8),
      }),
    })),
])

/**
 * 確定 metadata の実差分を既存 Action へ変換する。未知の資産形式は解析失敗とする。
 * @param params 生取引、全登録アドレス、MPT 発行 ID 別の確認済み AssetScale
 * @returns 根拠のある売買・流動性操作、要確認の資産移動、分離した手数料
 */
export const analyzeXrplTransaction = (params: {
  transaction: unknown
  addresses: ReadonlySet<string>
  mptScales: ReadonlyMap<string, number>
}): readonly Action[] => {
  const tx = xrplTransactionSchema.parse(params.transaction)
  const ambiguousAssets = new Set<string>()
  const deltas = new Map<
    string,
    { asset: CryptoCurrency | Nft; owner: string; amount: BigDecimal }
  >()
  /** 資産別・所有者別に差分を加算して、NFT ページ再配置を相殺する。 */
  const add = (input: {
    asset: CryptoCurrency | Nft
    owner: string
    amount: BigDecimal
  }) => {
    const key = `${input.owner}:${input.asset.id}`
    const previous = deltas.get(key)
    deltas.set(key, {
      ...input,
      amount:
        previous === undefined
          ? input.amount
          : plus(previous.amount, input.amount),
    })
  }
  for (const entry of tx.meta.AffectedNodes) {
    const kinds = ['CreatedNode', 'ModifiedNode', 'DeletedNode'].filter(
      (kind) => entry[kind] !== undefined,
    )
    if (kinds.length !== 1) throw new Error('Invalid XRPL affected node')
    const kind = kinds[0]
    const node = nodeSchema.parse(entry[kind])
    const final = kind === 'CreatedNode' ? node.NewFields : node.FinalFields
    if (
      final === undefined &&
      kind === 'ModifiedNode' &&
      node.PreviousFields === undefined
    )
      continue
    if (final === undefined) throw new Error('Missing XRPL ledger fields')
    const before =
      kind === 'CreatedNode' ? undefined : { ...final, ...node.PreviousFields }
    const after = kind === 'DeletedNode' ? undefined : final
    switch (node.LedgerEntryType) {
      case 'AccountRoot': {
        const owner = xrplClassicAddressSchema.parse(final.Account)
        const oldBalance =
          before === undefined
            ? 0n
            : BigInt(z.string().regex(/^\d+$/).parse(before.Balance))
        const newBalance =
          after === undefined
            ? 0n
            : BigInt(z.string().regex(/^\d+$/).parse(after.Balance))
        add({
          owner,
          asset: xrplXrp,
          amount: { value: newBalance - oldBalance, decimals: 6 },
        })
        break
      }
      case 'RippleState': {
        const low = issueSchema.parse(final.LowLimit)
        const high = issueSchema.parse(final.HighLimit)
        const oldValue =
          before === undefined
            ? createBigDecimal(0n)
            : createBigDecimal(balanceSchema.parse(before.Balance).value)
        const newValue =
          after === undefined
            ? createBigDecimal(0n)
            : createBigDecimal(balanceSchema.parse(after.Balance).value)
        for (const [owner, issuer, sign] of [
          [low.issuer, high.issuer, 1n],
          [high.issuer, low.issuer, -1n],
        ] as const) {
          // 負残高は相手方への発行債務。保有トークン数量と混同しない。
          const oldHolding = {
            ...oldValue,
            value: oldValue.value * sign > 0n ? oldValue.value * sign : 0n,
          }
          const newHolding = {
            ...newValue,
            value: newValue.value * sign > 0n ? newValue.value * sign : 0n,
          }
          const amount = minus(newHolding, oldHolding)
          const currency =
            low.currency.length === 40
              ? low.currency.toUpperCase()
              : low.currency
          add({
            owner,
            asset: {
              type: 'CryptoCurrency',
              id: `xrpl:iou:${issuer}:${currency}`,
              name: `${currency} (${issuer})`,
              symbol: currency,
              updatedAt: 0,
            },
            amount,
          })
        }
        break
      }
      case 'MPToken': {
        const owner = xrplClassicAddressSchema.parse(final.Account)
        const id = z
          .string()
          .regex(/^[A-Fa-f0-9]{48}$/)
          .parse(final.MPTokenIssuanceID)
          .toUpperCase()
        const scale = z
          .number()
          .int()
          .min(0)
          .max(255)
          .optional()
          .parse(params.mptScales.get(id))
        if (scale === undefined && !params.addresses.has(owner)) break
        if (scale === undefined)
          throw new Error(`Missing XRPL MPT scale: ${id}`)
        // 非公開数量を推測せず、再解析可能な失敗として扱う。
        if (
          Object.keys(final).some((key) =>
            /Confidential|Encrypted|Inbox/.test(key),
          )
        ) {
          if (!params.addresses.has(owner)) {
            ambiguousAssets.add(`xrpl:mpt:${id}`)
            break
          }
          throw new Error('Unsupported confidential XRPL MPT balance')
        }
        const oldValue =
          before === undefined
            ? 0n
            : BigInt(
                z
                  .string()
                  .regex(/^\d+$/)
                  .parse(before.MPTAmount ?? '0'),
              )
        const newValue =
          after === undefined
            ? 0n
            : BigInt(
                z
                  .string()
                  .regex(/^\d+$/)
                  .parse(after.MPTAmount ?? '0'),
              )
        add({
          owner,
          asset: {
            type: 'CryptoCurrency',
            id: `xrpl:mpt:${id}`,
            name: `MPT ${id}`,
            symbol: `MPT ${id.slice(0, 8)}`,
            updatedAt: 0,
          },
          amount: { value: newValue - oldValue, decimals: scale },
        })
        break
      }
      case 'NFTokenPage': {
        const owner = createXrplAddressFromAccountId({
          accountId: node.LedgerIndex.slice(0, 40),
        })
        for (const [fields, sign] of [
          [before, -1n],
          [after, 1n],
        ] as const) {
          if (fields === undefined) continue
          const nfts = z
            .array(
              z.object({
                NFToken: z.object({
                  NFTokenID: z.string().regex(/^[A-Fa-f0-9]{64}$/),
                }),
              }),
            )
            .parse(fields.NFTokens ?? [])
          for (const nft of nfts) {
            const id = nft.NFToken.NFTokenID.toUpperCase()
            add({
              owner,
              asset: {
                type: 'Nft',
                id: `xrpl:nft:${id}`,
                name: `XRPL NFT ${id}`,
                updatedAt: 0,
              },
              amount: { value: sign, decimals: 0 },
            })
          }
        }
        break
      }
      default:
        break
    }
  }
  const networkFee = BigInt(tx.tx_json.Fee)
  const fee = params.addresses.has(tx.tx_json.Account) ? networkFee : 0n
  if (fee > 0n && !deltas.has(`${tx.tx_json.Account}:${xrplXrp.id}`)) {
    throw new Error('XRPL fee payer balance metadata is missing')
  }
  if (networkFee > 0n && deltas.has(`${tx.tx_json.Account}:${xrplXrp.id}`))
    add({
      owner: tx.tx_json.Account,
      asset: xrplXrp,
      amount: { value: networkFee, decimals: 6 },
    })
  const assetDeltas = new Map<string, { owner: string; amount: BigDecimal }[]>()
  for (const delta of deltas.values()) {
    if (delta.amount.value === 0n) continue
    const entries = assetDeltas.get(delta.asset.id) ?? []
    entries.push(delta)
    assetDeltas.set(delta.asset.id, entries)
  }
  const counterparties = new Map<string, string>()
  for (const [assetId, entries] of assetDeltas) {
    if (ambiguousAssets.has(assetId)) continue
    let total = createBigDecimal(0n)
    for (const entry of entries) total = plus(total, entry.amount)
    if (total.value !== 0n) continue
    const senders = entries.filter((entry) => entry.amount.value < 0n)
    const recipients = entries.filter((entry) => entry.amount.value > 0n)
    if (senders.length === 1) {
      for (const recipient of recipients)
        counterparties.set(`${assetId}:${recipient.owner}`, senders[0].owner)
    }
    if (recipients.length === 1) {
      for (const sender of senders)
        counterparties.set(`${assetId}:${sender.owner}`, recipients[0].owner)
    }
  }
  // 同一資産の直接 Payment は、送金手数料で数量が一致しない保有者間送金も端点で補完する。
  const destination = xrplClassicAddressSchema.safeParse(tx.tx_json.Destination)
  const paymentAsset = paymentAssetSchema.safeParse(
    tx.meta.delivered_amount ?? tx.tx_json.DeliverMax ?? tx.tx_json.Amount,
  )
  const sendAsset =
    tx.tx_json.SendMax === undefined
      ? paymentAsset
      : paymentAssetSchema.safeParse(tx.tx_json.SendMax)
  const paymentDeltas = paymentAsset.success
    ? (assetDeltas.get(paymentAsset.data.id) ?? [])
    : []
  const paymentSender = paymentDeltas.find(
    (entry) => entry.owner === tx.tx_json.Account && entry.amount.value < 0n,
  )
  const paymentRecipient = destination.success
    ? paymentDeltas.find(
        (entry) => entry.owner === destination.data && entry.amount.value > 0n,
      )
    : undefined
  const onlyPaymentEndpoints =
    destination.success &&
    paymentDeltas.length > 0 &&
    paymentDeltas.every(
      (entry) =>
        (entry.owner === tx.tx_json.Account && entry.amount.value < 0n) ||
        (entry.owner === destination.data && entry.amount.value > 0n),
    )
  const holderPayment =
    paymentSender !== undefined &&
    paymentRecipient !== undefined &&
    plus(paymentSender.amount, paymentRecipient.amount).value <= 0n
  const directPayment =
    tx.meta.TransactionResult === 'tesSUCCESS' &&
    tx.tx_json.TransactionType === 'Payment' &&
    destination.success &&
    destination.data !== tx.tx_json.Account &&
    (tx.tx_json.Paths === undefined ||
      (Array.isArray(tx.tx_json.Paths) && tx.tx_json.Paths.length === 0)) &&
    paymentAsset.success &&
    sendAsset.success &&
    paymentAsset.data.id === sendAsset.data.id &&
    onlyPaymentEndpoints &&
    (paymentAsset.data.id === xrplXrp.id ||
      paymentAsset.data.issuer === tx.tx_json.Account ||
      paymentAsset.data.issuer === destination.data ||
      holderPayment)
      ? {
          assetId: paymentAsset.data.id,
          from: tx.tx_json.Account,
          to: destination.data,
        }
      : undefined
  const transfers: Transfer[] = []
  for (const delta of [...deltas.values()].sort((a, b) =>
    `${a.asset.id}:${a.owner}`.localeCompare(`${b.asset.id}:${b.owner}`),
  )) {
    if (!params.addresses.has(delta.owner)) continue
    if (delta.amount.value === 0n) continue
    const pair =
      !ambiguousAssets.has(delta.asset.id) &&
      directPayment?.assetId === delta.asset.id
        ? directPayment
        : undefined
    const counterparty =
      counterparties.get(`${delta.asset.id}:${delta.owner}`) ??
      (delta.amount.value > 0n
        ? pair?.to === delta.owner
          ? pair.from
          : undefined
        : pair?.from === delta.owner
          ? pair.to
          : undefined)
    transfers.push({
      direction: delta.amount.value > 0n ? 'in' : 'out',
      ...(delta.amount.value > 0n
        ? { from: counterparty, to: delta.owner }
        : { from: delta.owner, to: counterparty }),
      asset: delta.asset,
      amount: {
        ...delta.amount,
        value:
          delta.amount.value < 0n ? -delta.amount.value : delta.amount.value,
      },
    })
  }
  const source = createXrplSourceId({ hash: tx.hash })
  const timestamp = Date.parse(tx.close_time_iso)
  const actions: Action[] = []
  const incoming = transfers.filter((transfer) => transfer.direction === 'in')
  const outgoing = transfers.filter((transfer) => transfer.direction === 'out')
  const success = tx.meta.TransactionResult === 'tesSUCCESS'
  const operation = tx.tx_json.TransactionType
  const sameAssetInternal =
    incoming.length === 1 &&
    outgoing.length === 1 &&
    incoming[0].asset.id === outgoing[0].asset.id
  // 取引名だけでは約定とみなさず、対価を含む確定差分があるときだけ分類する。
  const type =
    success && incoming.length > 0 && outgoing.length > 0 && !sameAssetInternal
      ? operation === 'AMMDeposit'
        ? 'add-liquidity'
        : operation === 'AMMWithdraw'
          ? 'remove-liquidity'
          : ['OfferCreate', 'Payment', 'NFTokenAcceptOffer'].includes(operation)
            ? incoming.length === 1 && incoming[0].asset.type === 'Nft'
              ? 'buy-nft'
              : outgoing.length === 1 && outgoing[0].asset.type === 'Nft'
                ? 'sell-nft'
                : 'trade'
            : undefined
      : undefined
  if (type !== undefined && transfers.length > 0) {
    actions.push({
      source,
      timestamp,
      order: 0,
      evidence: 'system-rule',
      type,
      transfers,
    })
  } else {
    // 分類未確定の増減は Transfer ごとに記録し、入出金方向で既存型へ接続する。
    for (const transfer of transfers) {
      actions.push({
        source,
        timestamp,
        order: actions.length,
        evidence: 'none',
        type: transfer.direction === 'in' ? 'gift' : 'transfer',
        transfers: [transfer],
      })
    }
  }
  if (fee > 0n)
    actions.push({
      source,
      timestamp,
      order: Math.max(1, actions.length),
      type: 'transaction-fee',
      evidence: 'system-rule',
      transfers: [
        {
          direction: 'out',
          from: tx.tx_json.Account,
          asset: xrplXrp,
          amount: { value: fee, decimals: 6 },
        },
      ],
    })
  return actions
}
